import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { hasCoPowers } from '@cosvision/shared';
import clsx from 'clsx';
import type {
  CreateTransactionRequest,
  FundResponse,
  SubHeadResponse,
  TransactionResponse,
  TransactionDirection,
  UpdateTransactionRequest,
} from '@cosvision/shared';
import { useAuthStore } from '../store/auth-store.js';
import { useFundsQuery } from '../hooks/useFunds.js';
import { useSubHeadsQuery } from '../hooks/useSubHeads.js';
import { useDhakaToday } from '../hooks/useDhakaToday.js';
import { ValidationBanner } from '../components/forms/ValidationBanner.js';
import { Skeleton } from '../components/dashboard/Skeleton.js';
import {
  useCreateTransactionMutation,
  useDeleteTransactionMutation,
  useTransactionsQuery,
  useUpdateTransactionMutation,
} from '../hooks/useTransactions.js';
import { ApiClientError } from '../lib/api.js';
import { formatBDT, formatDateTime } from '../lib/format.js';
import { useIsMobile } from '../hooks/useMediaQuery.js';
import { useMobileTopBar } from '../hooks/useMobileTopBar.js';

interface FormState {
  fundId: string;
  /** Empty string means "no sub-head" (a valid choice for an unscoped entry). */
  subHeadId: string;
  voucherNo: string;
  transactionDate: string;
  direction: TransactionDirection;
  amount: string;
  narration: string;
}

function emptyForm(defaultDate: string): FormState {
  return {
    fundId: '',
    subHeadId: '',
    voucherNo: '',
    transactionDate: defaultDate,
    direction: 'DEBIT',
    amount: '',
    narration: '',
  };
}

function formFromTransaction(tx: TransactionResponse): FormState {
  return {
    fundId: tx.fundId,
    subHeadId: tx.subHeadId ?? '',
    voucherNo: tx.voucherNo,
    transactionDate: tx.transactionDate,
    direction: tx.direction,
    amount: tx.amount,
    narration: tx.narration,
  };
}

export function EntryPage() {
  const today = useDhakaToday();
  const user = useAuthStore((s) => s.user);
  const isCo = hasCoPowers(user);
  const isMobile = useIsMobile();

  const [form, setForm] = useState<FormState>(() => emptyForm(today));
  const [editing, setEditing] = useState<TransactionResponse | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [lastSavedNote, setLastSavedNote] = useState<string | null>(null);

  const fundsQ = useFundsQuery();
  const activeFunds = useMemo<FundResponse[]>(
    () => (fundsQ.data ?? []).filter((f) => f.isActive),
    [fundsQ.data],
  );

  // When editing, include inactive sub-heads so a previously-chosen one that
  // has since been deactivated still appears in the dropdown — otherwise the
  // value would silently fall back to "— None —" and a save would unset it.
  const subHeadsQ = useSubHeadsQuery(form.fundId || null, {
    includeInactive: editing !== null,
  });
  const visibleSubHeads = useMemo<SubHeadResponse[]>(() => subHeadsQ.data ?? [], [subHeadsQ.data]);

  const todayQuery = user
    ? { enteredByUserId: user.id, dateFrom: today, dateTo: today, page: 1, limit: 50 }
    : {};
  const todayQ = useTransactionsQuery(todayQuery, { enabled: Boolean(user) });

  const createMut = useCreateTransactionMutation();
  const updateMut = useUpdateTransactionMutation();
  const deleteMut = useDeleteTransactionMutation();
  const submitting = createMut.isPending || updateMut.isPending;

  // Auto-clear the saved banner after a few seconds.
  useEffect(() => {
    if (!lastSavedNote) return;
    const id = window.setTimeout(() => setLastSavedNote(null), 3500);
    return () => window.clearTimeout(id);
  }, [lastSavedNote]);

  // When the fund changes, drop any selected sub-head that doesn't belong to it.
  useEffect(() => {
    if (!form.subHeadId) return;
    if (subHeadsQ.data && !subHeadsQ.data.some((s) => s.id === form.subHeadId)) {
      setForm((f) => ({ ...f, subHeadId: '' }));
    }
  }, [form.subHeadId, subHeadsQ.data]);

  function applyApiError(err: unknown): void {
    if (err instanceof ApiClientError) {
      setFormError(err.message);
      setFieldErrors(err.errors ?? {});
    } else {
      setFormError(err instanceof Error ? err.message : 'Save failed');
      setFieldErrors({});
    }
  }

  function buildCreatePayload(): CreateTransactionRequest {
    return {
      fundId: form.fundId,
      subHeadId: form.subHeadId ? form.subHeadId : null,
      voucherNo: form.voucherNo.trim(),
      transactionDate: form.transactionDate,
      direction: form.direction,
      amount: form.amount.trim(),
      narration: form.narration.trim(),
    };
  }

  function buildUpdatePayload(): UpdateTransactionRequest {
    return {
      subHeadId: form.subHeadId ? form.subHeadId : null,
      voucherNo: form.voucherNo.trim(),
      transactionDate: form.transactionDate,
      direction: form.direction,
      amount: form.amount.trim(),
      narration: form.narration.trim(),
    };
  }

  async function save(mode: 'new' | 'close'): Promise<void> {
    setFormError(null);
    setFieldErrors({});
    try {
      if (editing) {
        const updated = await updateMut.mutateAsync({
          id: editing.id,
          input: buildUpdatePayload(),
        });
        setLastSavedNote(`Updated voucher ${updated.voucherNo}`);
        setEditing(null);
        setForm(emptyForm(today));
      } else {
        const created = await createMut.mutateAsync(buildCreatePayload());
        setLastSavedNote(`Posted voucher ${created.voucherNo} — ${formatBDT(created.amount)}`);
        if (mode === 'new') {
          // Keep fund/sub-head/direction/date — clear identifying fields.
          setForm((f) => ({ ...f, voucherNo: '', amount: '', narration: '' }));
        } else {
          setForm(emptyForm(today));
        }
      }
    } catch (err) {
      applyApiError(err);
    }
  }

  function onSubmitNew(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    void save('new');
  }

  function onSaveAndClose(): void {
    void save('close');
  }

  function onCancelEdit(): void {
    setEditing(null);
    setForm(emptyForm(today));
    setFormError(null);
    setFieldErrors({});
  }

  function onEditRow(row: TransactionResponse): void {
    setEditing(row);
    setForm(formFromTransaction(row));
    setFormError(null);
    setFieldErrors({});
    setLastSavedNote(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function onDeleteRow(row: TransactionResponse): Promise<void> {
    if (!window.confirm(`Delete voucher ${row.voucherNo}? This cannot be undone.`)) return;
    try {
      await deleteMut.mutateAsync(row.id);
      setLastSavedNote(`Deleted voucher ${row.voucherNo}`);
    } catch (err) {
      applyApiError(err);
    }
  }

  const fieldErr = (key: string): string | undefined => fieldErrors[key]?.[0];

  const previewAmount = useMemo(() => {
    const cleaned = form.amount.trim();
    if (!cleaned) return null;
    const n = Number.parseFloat(cleaned);
    if (Number.isNaN(n) || n <= 0) return null;
    return formatBDT(n);
  }, [form.amount]);

  // Mobile: the top bar carries "Data Entry" + context; the in-page header is
  // dropped so the form starts immediately under the bar.
  useMobileTopBar({
    subtitle: isMobile
      ? editing
        ? `Editing ${editing.voucherNo}`
        : 'Post a transaction'
      : undefined,
  });

  return (
    <div className="flex min-h-full flex-col gap-6">
      {isMobile ? null : (
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Data Entry</h1>
            <p className="text-sm text-slate-500">
              {editing
                ? `Editing voucher ${editing.voucherNo}.`
                : 'Post a transaction to a fund. Use Save & New to keep posting in the same context.'}
            </p>
          </div>
          <div className="text-sm text-slate-500">{formatDateTime(new Date(), 'DD MMM YYYY')}</div>
        </div>
      )}

      {lastSavedNote && (
        <div
          role="status"
          className="rounded-md bg-rag-green-soft border border-rag-green/30 px-4 py-2 text-sm text-rag-green"
        >
          {lastSavedNote}
        </div>
      )}

      {fundsQ.isPending ? (
        // Skeleton matches the form's vertical rhythm so nothing shifts when
        // real data lands. Without this the main column briefly painted
        // blank after login on a slow tablet (BUG-07).
        <div className="card space-y-5" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading entry form…</span>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
          <Skeleton className="h-20 w-full" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-28" />
          </div>
        </div>
      ) : (
        <form className="card space-y-5" onSubmit={onSubmitNew} noValidate>
          <ValidationBanner errorCount={Object.keys(fieldErrors).length} />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="fundId" className="form-label">
                Fund
              </label>
              <select
                id="fundId"
                className="form-input"
                value={form.fundId}
                onChange={(e) => setForm((f) => ({ ...f, fundId: e.target.value }))}
                disabled={submitting || fundsQ.isLoading}
                required
                aria-invalid={fieldErr('fundId') ? 'true' : 'false'}
              >
                <option value="">— Select fund —</option>
                {activeFunds.map((fund) => (
                  <option key={fund.id} value={fund.id}>
                    {fund.code} — {fund.name}
                  </option>
                ))}
              </select>
              {fieldErr('fundId') && <p className="form-error">{fieldErr('fundId')}</p>}
            </div>

            <div>
              <label htmlFor="subHeadId" className="form-label">
                Sub-head <span className="text-xs font-normal text-slate-400">(optional)</span>
              </label>
              <select
                id="subHeadId"
                className="form-input"
                value={form.subHeadId}
                onChange={(e) => setForm((f) => ({ ...f, subHeadId: e.target.value }))}
                disabled={submitting || !form.fundId || subHeadsQ.isLoading}
                aria-invalid={fieldErr('subHeadId') ? 'true' : 'false'}
              >
                <option value="">— None —</option>
                {visibleSubHeads.map((sh) => (
                  <option key={sh.id} value={sh.id}>
                    {sh.code} — {sh.name}
                    {sh.isActive ? '' : ' (inactive)'}
                  </option>
                ))}
              </select>
              {fieldErr('subHeadId') && <p className="form-error">{fieldErr('subHeadId')}</p>}
            </div>

            <div>
              <label htmlFor="transactionDate" className="form-label">
                Date
              </label>
              <input
                id="transactionDate"
                type="date"
                className="form-input"
                value={form.transactionDate}
                max={today}
                onChange={(e) => setForm((f) => ({ ...f, transactionDate: e.target.value }))}
                disabled={submitting}
                required
                aria-invalid={fieldErr('transactionDate') ? 'true' : 'false'}
              />
              {fieldErr('transactionDate') && (
                <p className="form-error">{fieldErr('transactionDate')}</p>
              )}
            </div>

            <div>
              <label htmlFor="voucherNo" className="form-label">
                Voucher No
              </label>
              <input
                id="voucherNo"
                type="text"
                className="form-input"
                value={form.voucherNo}
                onChange={(e) => setForm((f) => ({ ...f, voucherNo: e.target.value }))}
                disabled={submitting}
                placeholder="e.g. V-2026-001"
                maxLength={50}
                required
                aria-invalid={fieldErr('voucherNo') ? 'true' : 'false'}
              />
              {fieldErr('voucherNo') && <p className="form-error">{fieldErr('voucherNo')}</p>}
            </div>

            <div>
              <span className="form-label">Direction</span>
              <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Direction">
                <DirectionToggle
                  value={form.direction}
                  disabled={submitting}
                  onChange={(d) => setForm((f) => ({ ...f, direction: d }))}
                />
              </div>
              {fieldErr('direction') && <p className="form-error">{fieldErr('direction')}</p>}
            </div>

            <div>
              <label htmlFor="amount" className="form-label">
                Amount (BDT)
              </label>
              <input
                id="amount"
                type="text"
                inputMode="decimal"
                className="form-input tabular-nums"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                disabled={submitting}
                placeholder="0.00"
                autoComplete="off"
                required
                aria-invalid={fieldErr('amount') ? 'true' : 'false'}
              />
              <p className="mt-1 text-xs text-slate-500 tabular-nums">
                {previewAmount ? `Will post as ${previewAmount}` : 'Up to 2 decimal places.'}
              </p>
              {fieldErr('amount') && <p className="form-error">{fieldErr('amount')}</p>}
            </div>

            <div className="md:col-span-2">
              <label htmlFor="narration" className="form-label">
                Narration <span className="text-xs font-normal text-slate-400">(optional)</span>
              </label>
              <textarea
                id="narration"
                className="form-input"
                rows={2}
                maxLength={500}
                value={form.narration}
                onChange={(e) => setForm((f) => ({ ...f, narration: e.target.value }))}
                disabled={submitting}
                placeholder="Free-text description — appears on reports."
                aria-invalid={fieldErr('narration') ? 'true' : 'false'}
              />
              <p className="mt-1 text-xs text-slate-500">{form.narration.length}/500</p>
              {fieldErr('narration') && <p className="form-error">{fieldErr('narration')}</p>}
            </div>
          </div>

          {formError && Object.keys(fieldErrors).length === 0 && (
            <div role="alert" className="rounded-md bg-rag-red-soft px-3 py-2 text-sm text-rag-red">
              {formError}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            {editing ? (
              <>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving…' : 'Save changes'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onCancelEdit}
                  disabled={submitting}
                >
                  Cancel edit
                </button>
              </>
            ) : (
              <>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving…' : 'Save & New'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onSaveAndClose}
                  disabled={submitting}
                >
                  Save & Close
                </button>
              </>
            )}
          </div>
        </form>
      )}

      <TodayList
        rows={todayQ.data?.data ?? []}
        loading={todayQ.isLoading}
        error={todayQ.error}
        today={today}
        canDelete={isCo}
        onEdit={onEditRow}
        onDelete={(row) => {
          void onDeleteRow(row);
        }}
      />
    </div>
  );
}

function DirectionToggle({
  value,
  disabled,
  onChange,
}: {
  value: TransactionDirection;
  disabled: boolean;
  onChange: (d: TransactionDirection) => void;
}) {
  const directions: { code: TransactionDirection; label: string }[] = [
    { code: 'CREDIT', label: 'Credit (in)' },
    { code: 'DEBIT', label: 'Debit (out)' },
  ];
  return (
    <>
      {directions.map((d, i) => (
        <button
          key={d.code}
          type="button"
          aria-pressed={value === d.code ? 'true' : 'false'}
          disabled={disabled}
          onClick={() => onChange(d.code)}
          className={clsx(
            'px-4 py-2 text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-bn-500',
            i === 0 ? 'rounded-l-md' : 'rounded-r-md -ml-px',
            value === d.code
              ? 'bg-bn-800 text-white border-bn-800'
              : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          {d.label}
        </button>
      ))}
    </>
  );
}

function TodayList({
  rows,
  loading,
  error,
  today,
  canDelete,
  onEdit,
  onDelete,
}: {
  rows: TransactionResponse[];
  loading: boolean;
  error: unknown;
  today: string;
  canDelete: boolean;
  onEdit: (row: TransactionResponse) => void;
  onDelete: (row: TransactionResponse) => void;
}) {
  // Hide the SUB-HEAD column when none of today's entries used one — most
  // entries don't (BUG-08). Once an operator posts a sub-head entry the
  // column reappears so they can see what they tagged.
  const todayHasSubHead = rows.some((r) => r.subHeadId !== null);
  const isMobile = useIsMobile();

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Your entries today</h2>
        <span className="text-xs text-slate-500">{today}</span>
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-slate-500">Loading…</p>
      ) : error ? (
        <p className="mt-4 text-sm text-rag-red">
          {error instanceof Error ? error.message : 'Failed to load entries.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">
          No entries yet today. Use the form above to post one.
        </p>
      ) : isMobile ? (
        <ul className="mt-3 divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.id} className="flex items-start gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-semibold text-slate-900">{r.voucherNo}</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">
                    {r.fundCode}
                  </span>
                  <span
                    className={clsx(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
                      r.direction === 'CREDIT'
                        ? 'bg-rag-green-soft text-rag-green'
                        : 'bg-rag-red-soft text-rag-red',
                    )}
                  >
                    {r.direction === 'CREDIT' ? 'CR' : 'DR'}
                  </span>
                </div>
                {r.narration ? (
                  <p className="mt-1 truncate text-xs text-slate-500">{r.narration}</p>
                ) : null}
                <p className="mt-0.5 text-[11px] tabular-nums text-slate-400">
                  {formatDateTime(r.createdAt, 'HH:mm')}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <span
                  className={clsx(
                    'text-sm font-semibold tabular-nums',
                    r.direction === 'CREDIT' ? 'text-rag-green' : 'text-slate-900',
                  )}
                >
                  {r.direction === 'CREDIT' ? '+' : '−'}
                  {formatBDT(r.amount)}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="rounded-lg px-2.5 py-1 text-xs font-medium text-bn-700 transition active:bg-bn-50"
                    onClick={() => onEdit(r)}
                  >
                    Edit
                  </button>
                  {canDelete ? (
                    <button
                      type="button"
                      className="rounded-lg px-2.5 py-1 text-xs font-medium text-rag-red transition active:bg-rag-red-soft"
                      onClick={() => onDelete(r)}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-2 pr-3">Voucher</th>
                <th className="py-2 pr-3">Fund</th>
                {todayHasSubHead ? <th className="py-2 pr-3">Sub-head</th> : null}
                <th className="py-2 pr-3">Dir.</th>
                <th className="py-2 pr-3 text-right">Amount</th>
                <th className="py-2 pr-3">Narration</th>
                <th className="py-2 pr-3">Posted</th>
                <th className="py-2 pr-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="align-top">
                  <td className="py-2 pr-3 font-medium text-slate-900">{r.voucherNo}</td>
                  <td className="py-2 pr-3 text-slate-700">{r.fundCode}</td>
                  {todayHasSubHead ? (
                    <td className="py-2 pr-3 text-slate-700">{r.subHeadCode ?? '—'}</td>
                  ) : null}
                  <td className="py-2 pr-3">
                    <span
                      className={clsx(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        r.direction === 'CREDIT'
                          ? 'bg-rag-green-soft text-rag-green'
                          : 'bg-rag-red-soft text-rag-red',
                      )}
                    >
                      {r.direction === 'CREDIT' ? 'CR' : 'DR'}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                    {formatBDT(r.amount)}
                  </td>
                  <td className="py-2 pr-3 text-slate-600 max-w-xs truncate" title={r.narration}>
                    {r.narration || '—'}
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-500">
                    {formatDateTime(r.createdAt, 'HH:mm')}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      className="text-xs font-medium text-bn-700 hover:text-bn-900"
                      onClick={() => onEdit(r)}
                    >
                      Edit
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        className="ml-3 text-xs font-medium text-rag-red hover:underline"
                        onClick={() => onDelete(r)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
